import { format } from 'date-fns';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import compensationService, {
  Compensation,
  CreateCompensationProps,
  CreateCompensationResponse,
  EditCompensationProps,
} from '@/services/compensation';
import StaffMemberCompensationRowComponent, {
  FormData,
} from './StaffMemberCompensationRow.component';

type Props = {
  staffMemberId: string;
  isEditing?: boolean;
  compensation?: Compensation;
  onAddNewCompensation?: (response: CreateCompensationResponse) => void;
  onEditCompensation?: (response: CreateCompensationResponse) => void;
  onEditClick?: () => void;
};

const StaffMemberCompensationRow = ({
  staffMemberId,
  compensation,
  isEditing,
  onAddNewCompensation,
  onEditCompensation,
  onEditClick,
}: Props): JSX.Element => {
  const toast = useToastContext();

  const createCompensationMutation = useApiSubmit<
    CreateCompensationResponse,
    CreateCompensationProps
  >(compensationService.createCompensation);

  const editCompensationMutation = useApiSubmit<CreateCompensationResponse, EditCompensationProps>(
    compensationService.editCompensation
  );

  const handleSave = async ({ startDate, endDate, ...data }: FormData): Promise<void> => {
    try {
      const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;
      if (compensation) {
        const editedCompensation = await editCompensationMutation.mutateAsync({
          id: compensation.id,
          staffMemberId,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: formattedEndDate,
          ...data,
        });

        onEditCompensation?.(editedCompensation);
        toast.setMessage('Compensation edited successfully.');
        toast.setSeverity('success');
        toast.showToast();
      } else {
        const newCompensation = await createCompensationMutation.mutateAsync({
          staffMemberId,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: formattedEndDate,
          ...data,
        });

        onAddNewCompensation?.(newCompensation);
        toast.setMessage('Compensation added successfully.');
        toast.setSeverity('success');
        toast.showToast();
      }
    } catch (error) {
      toast.setMessage(
        `There was an error ${compensation ? 'editing' : 'creating'} the compensation.`
      );
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  return (
    <StaffMemberCompensationRowComponent
      compensation={compensation}
      isEditing={isEditing}
      onSave={handleSave}
      isLoading={createCompensationMutation.isLoading || editCompensationMutation.isLoading}
      onEditClick={onEditClick}
    />
  );
};

export default StaffMemberCompensationRow;
